import moment from 'moment-timezone';

export const YELUM_COMPETENCE_TIMEZONE = 'America/Sao_Paulo';

export enum YelumCompetence {
  CURRENT_MONTH = 'CURRENT_MONTH',
  NEXT_MONTH = 'NEXT_MONTH',
}

export enum YelumCompetenceFlow {
  IMMEDIATE = 'IMMEDIATE',
  SCHEDULED = 'SCHEDULED',
}

export interface YelumCompetenceDates {
  policyPeriodStart: Date;
  policyPeriodEnd: Date;
}

export interface YelumCompetenceOption {
  value: YelumCompetence;
  label: string;
  startDate: string;
  endDate: string;
  flow: YelumCompetenceFlow;
}

const MONTH_NAMES_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function toSaoPauloMoment(date?: Date | string): moment.Moment {
  return moment.tz(date ?? undefined, YELUM_COMPETENCE_TIMEZONE).startOf('day');
}

function formatCompetenceLabel(monthMoment: moment.Moment, suffix: string): string {
  const monthName = MONTH_NAMES_PT[monthMoment.month()];
  return `${monthName}/${monthMoment.year()} — ${suffix}`;
}

export function resolveYelumCompetenceDates(
  competence: YelumCompetence,
  referenceDate: Date | string = new Date(),
): YelumCompetenceDates {
  const reference = toSaoPauloMoment(referenceDate);

  const policyPeriodStart =
    competence === YelumCompetence.CURRENT_MONTH
      ? reference.clone().startOf('month').toDate()
      : reference.clone().add(1, 'month').startOf('month').toDate();

  const policyPeriodEnd = moment
    .tz(policyPeriodStart, YELUM_COMPETENCE_TIMEZONE)
    .add(365, 'days')
    .toDate();

  return { policyPeriodStart, policyPeriodEnd };
}

export function inferYelumCompetenceFromDate(
  date: Date | string,
  referenceDate: Date | string = new Date(),
): YelumCompetence | null {
  const input = toSaoPauloMoment(date);
  const reference = toSaoPauloMoment(referenceDate);

  const currentMonthStart = reference.clone().startOf('month');
  const nextMonthStart = reference.clone().add(1, 'month').startOf('month');

  if (input.isSame(currentMonthStart, 'month')) {
    return YelumCompetence.CURRENT_MONTH;
  }

  if (input.isSame(nextMonthStart, 'month')) {
    return YelumCompetence.NEXT_MONTH;
  }

  return null;
}

export function getYelumCompetenceFlow(
  competence: YelumCompetence,
): YelumCompetenceFlow {
  return competence === YelumCompetence.CURRENT_MONTH
    ? YelumCompetenceFlow.IMMEDIATE
    : YelumCompetenceFlow.SCHEDULED;
}

export function buildYelumCompetenceOptions(
  referenceDate: Date | string = new Date(),
): { default: YelumCompetence; options: YelumCompetenceOption[] } {
  const reference = toSaoPauloMoment(referenceDate);
  const currentMonthStart = reference.clone().startOf('month');
  const nextMonthStart = reference.clone().add(1, 'month').startOf('month');

  const currentDates = resolveYelumCompetenceDates(
    YelumCompetence.CURRENT_MONTH,
    referenceDate,
  );
  const nextDates = resolveYelumCompetenceDates(
    YelumCompetence.NEXT_MONTH,
    referenceDate,
  );

  return {
    default: YelumCompetence.CURRENT_MONTH,
    options: [
      {
        value: YelumCompetence.CURRENT_MONTH,
        label: formatCompetenceLabel(currentMonthStart, 'mês atual'),
        startDate: moment
          .tz(currentDates.policyPeriodStart, YELUM_COMPETENCE_TIMEZONE)
          .format('YYYY-MM-DD'),
        endDate: moment
          .tz(currentDates.policyPeriodEnd, YELUM_COMPETENCE_TIMEZONE)
          .format('YYYY-MM-DD'),
        flow: YelumCompetenceFlow.IMMEDIATE,
      },
      {
        value: YelumCompetence.NEXT_MONTH,
        label: formatCompetenceLabel(nextMonthStart, 'próximo mês'),
        startDate: moment
          .tz(nextDates.policyPeriodStart, YELUM_COMPETENCE_TIMEZONE)
          .format('YYYY-MM-DD'),
        endDate: moment
          .tz(nextDates.policyPeriodEnd, YELUM_COMPETENCE_TIMEZONE)
          .format('YYYY-MM-DD'),
        flow: YelumCompetenceFlow.SCHEDULED,
      },
    ],
  };
}

export function applyYelumCompetenceToFormValues(
  competence: YelumCompetence,
  referenceDate: Date | string = new Date(),
) {
  const dates = resolveYelumCompetenceDates(competence, referenceDate);

  return {
    competence,
    startVigency: dates.policyPeriodStart,
    endVigency: dates.policyPeriodEnd,
  };
}

export function getCompetenceFlowLabel(competence: YelumCompetence): string {
  return competence === YelumCompetence.CURRENT_MONTH
    ? 'Contratação imediata — entra no lote atual'
    : 'Contratação agendada — processada no dia 1';
}
